import { useEffect, useState } from 'react';
import { getInstitutionSettings } from '../features/settings/api';

export type InstitutionBrand = {
  nama_pesantren: string;
  logo_url: string | null;
};

const fallback: InstitutionBrand = { nama_pesantren: 'Madinah Ar Rasul', logo_url: null };

// Singleton cache untuk mencegah multiple requests dari berbagai komponen
let cachedBrand: InstitutionBrand | null = null;
let brandPromise: Promise<InstitutionBrand> | null = null;

async function fetchInstitutionBrand(): Promise<InstitutionBrand> {
  // Return cache jika sudah ada
  if (cachedBrand) return cachedBrand;

  // Return promise yang sedang berjalan jika ada (deduplication)
  if (brandPromise) return brandPromise;

  // Buat request baru
  brandPromise = getInstitutionSettings()
    .then((brand) => {
      cachedBrand = brand;
      return brand;
    })
    .catch(() => {
      // Jangan cache fallback, biarkan retry di next call
      return fallback;
    })
    .finally(() => {
      // Clear promise setelah selesai
      brandPromise = null;
    });

  return brandPromise;
}

export function useInstitutionBrand() {
  const [brand, setBrand] = useState<InstitutionBrand>(cachedBrand ?? fallback);
  useEffect(() => {
    void fetchInstitutionBrand().then(setBrand);
  }, []);
  return brand;
}

// Helper untuk clear cache (mis. setelah update settings)
export function clearInstitutionBrandCache() {
  cachedBrand = null;
  brandPromise = null;
}

export function InstitutionLogo({ size = 40 }: { size?: number }) {
  const { logo_url, nama_pesantren } = useInstitutionBrand();
  if (logo_url) {
    return (
      <img
        src={logo_url}
        alt={`Logo ${nama_pesantren}`}
        width={size}
        height={size}
        style={{ objectFit: 'contain', borderRadius: '0.375rem', flex: '0 0 auto' }}
      />
    );
  }
  return (
    <div
      aria-label={`Placeholder logo ${nama_pesantren}`}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        flex: '0 0 auto',
        background: '#0F766E',
        color: '#fff',
        fontWeight: 800,
        fontSize: Math.max(12, Math.round(size * 0.38)),
      }}
    >
      MR
    </div>
  );
}
